import { useEffect, useState } from 'react';
import CustomModal from '../../../../../common-utilities/core/modal';
import { createUpdateExtraCharge } from '../apis';
import {
  CALCULATION_TYPE_OPTIONS,
  VALUE_MODE_OPTIONS,
  getModalData,
} from '../utils';
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';

const isEmpty = (value) => value === undefined || value === null || value === '';

const CreateUpdateModal = ({ showModal, title, closeModal, data, departmentList = [], onSuccess}) => {
  const [formDetails, setFormDetails] = useState({});

  const onInputChange = (event) => {
    setFormDetails({
      ...formDetails,
      [event.target.name]: event.target.value,
    });
  };

  const onCheckboxChange = (event) => {
    setFormDetails({
      ...formDetails,
      [event.target.name]: event.target.checked,
    });
  };

  useEffect(() => {
    getModalData(data, setFormDetails);
  }, [data]);

  useEffect(() => {
    if (!showModal) {
      setFormDetails({});
    }
  }, [showModal]);

  const isSaveDisabled =
    !formDetails.chargeCode ||
    !formDetails.chargeName ||
    !formDetails.calculationType ||
    !formDetails.valueMode ||
    !formDetails.departments?.length ||
    (formDetails.valueMode === 'CONFIGURED_VALUE' &&
      isEmpty(formDetails.defaultValue)) ||
    (formDetails.taxable && isEmpty(formDetails.taxRate));

  return (
    <>
      {showModal && (
        <CustomModal title={title} disabledOutsideClick={true} onClose={closeModal}>
          <div className="finance-v2-create-form">
            <TextField
              value={formDetails.chargeCode || ''}
              name="chargeCode"
              onChange={onInputChange}
              size="small"
              label="Charge Code"
              fullWidth
            />
            <TextField
              value={formDetails.chargeName || ''}
              name="chargeName"
              onChange={onInputChange}
              size="small"
              label="Charge Name"
              fullWidth
            />

            <TextField
              value={formDetails.hsnCode || ''}
              name="hsnCode"
              onChange={onInputChange}
              size="small"
              label="HSN Code"
              fullWidth
            />

            <Autocomplete
              options={CALCULATION_TYPE_OPTIONS}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Calculation Type" />
              )}
              value={
                CALCULATION_TYPE_OPTIONS.find(
                  (option) => option.value === formDetails?.calculationType
                ) || null
              }
              onChange={(e, val) => {
                setFormDetails({
                  ...formDetails,
                  calculationType: val?.value || null,
                });
              }}
            />

            <Autocomplete
              options={VALUE_MODE_OPTIONS}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Value Mode" />
              )}
              value={
                VALUE_MODE_OPTIONS.find(
                  (option) => option.value === formDetails?.valueMode
                ) || null
              }
              onChange={(e, val) => {
                setFormDetails({
                  ...formDetails,
                  valueMode: val?.value || null,
                });
              }}
            />

            {formDetails.valueMode === 'CONFIGURED_VALUE' && (
              <TextField
                value={formDetails.defaultValue ?? ''}
                name="defaultValue"
                type="number"
                onChange={onInputChange}
                size="small"
                label="Default Value"
                fullWidth
              />
            )}

            <FormControlLabel
              control={
                <Checkbox
                  name="taxable"
                  checked={!!formDetails.taxable}
                  onChange={onCheckboxChange}
                />
              }
              label="Taxable"
            />

            {formDetails.taxable && (
              <TextField
                value={formDetails.taxRate ?? ''}
                name="taxRate"
                type="number"
                onChange={onInputChange}
                size="small"
                label="Tax Rate (%)"
                fullWidth
              />
            )}

            <FormControlLabel
              control={
                <Checkbox
                  name="affectsPOTotal"
                  checked={!!formDetails.affectsPOTotal}
                  onChange={onCheckboxChange}
                />
              }
              label="Affects PO Total"
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="affectsBudget"
                  checked={!!formDetails.affectsBudget}
                  onChange={onCheckboxChange}
                />
              }
              label="Affects Budget"
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="isActive"
                  checked={!!formDetails.isActive}
                  onChange={onCheckboxChange}
                />
              }
              label="Is Active"
            />

            <Autocomplete
              multiple
              options={departmentList}
              value={departmentList.filter((dept) =>
                (formDetails.departments || []).some(
                  (id) => String(id) === String(dept.value)
                )
              )}
              onChange={(e, val) => {
                setFormDetails({
                  ...formDetails,
                  departments: (val || []).map((dept) => dept.value),
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Departments"
                  required
                  helperText="Extra Charges line is shown on PO creation only for these departments"
                />
              )}
            />

            <TextField
              value={formDetails.description || ''}
              name="description"
              onChange={onInputChange}
              size="small"
              label="Description"
              multiline
              minRows={2}
              fullWidth
            />

            <Button
              fullWidth
              variant="contained"
              disabled={isSaveDisabled}
              onClick={() => {
                createUpdateExtraCharge(formDetails, onSuccess);
              }}
            >
              {formDetails?.id ? 'Update' : 'Create'}
            </Button>
          </div>
        </CustomModal>
      )}
    </>
  );
};

export default CreateUpdateModal;
